// Image utilities for handling event legend icons and image error handling

/**
 * 获取通用图标的正确路径
 * 使用 new URL() 方式确保在开发和生产环境中都能正确加载
 */
const getScoreIconUrl = (filename: string): string => {
  try {
    return new URL(`/src/assets/icons/score/${filename}`, import.meta.url).href
  } catch (error) {
    console.warn(`Failed to load score icon: ${filename}`, error)
    // 降级到主题特定的图标
    try {
      return new URL(`@assets/icons/score/${filename}`, import.meta.url).href
    } catch (fallbackError) {
      console.warn(`Failed to load theme score icon: ${filename}`, fallbackError)
      return ''
    }
  }
}

/**
 * 获取主题相关图片资源的正确路径
 */
const getThemeImageUrl = (path: string): string => {
  try {
    return new URL(`@assets/${path}`, import.meta.url).href
  } catch (error) {
    console.warn(`Failed to load theme image: @assets/${path}`, error)
    return new URL('@assets/imgs/empty.webp', import.meta.url).href
  }
}

// Event legend icons configuration - 使用简单的路径方式
export const eventLegendIcons: any = {
  9: { key: 9, name: '进球', src: getScoreIconUrl('goal.webp') },
  0: { key: 0, name: '点球', src: getScoreIconUrl('penalty.webp') },
  7: { key: 0, name: '点球不进', src: getScoreIconUrl('penalty_miss.webp') },
  1: { key: 1, name: '比赛开始', src: getScoreIconUrl('whistle.webp') },
  34: { key: 34, name: '默认', src: getScoreIconUrl('default_score.webp') },
  2: { key: 0, name: '乌龙球', src: getScoreIconUrl('own_goal.webp') },
  30: { key: 30, name: '角球', src: getScoreIconUrl('corner.webp') },
  3: { key: 0, name: '助攻', src: getScoreIconUrl('assist.webp') },
  18: { key: 18, name: '黄牌', src: getScoreIconUrl('yellow_card.webp') },
  default: { key: 0, name: '红牌', src: getScoreIconUrl('red_card.webp') },
  21: { key: 21, name: '两黄一红', src: getScoreIconUrl('yellow_red_card.webp') },
  23: { key: 23, name: '换人', src: getScoreIconUrl('substitution.webp') },
  5: { key: 0, name: '受伤', src: getScoreIconUrl('injury.webp') },
  6: { key: 0, name: 'VAR', src: getScoreIconUrl('var.webp') },
}

// Image assets for fallback handling
export const imageAssets = {
  defaultScore: getScoreIconUrl('default_score.webp'),
  empty: getThemeImageUrl('imgs/empty.webp')
}

/**
 * Handle image loading errors with fallback logic
 * @param event - The error event from the img element
 * @param item - Optional item object with fallback property
 */
export const handleImageError = (event: Event, item?: any) => {
  const img = event.target as HTMLImageElement
  if (!img) return

  // First try item-specific fallback
  if (item?.fallback && img.src !== item.fallback) {
    console.warn(`图片加载失败，使用备用图片: ${item.name || 'unknown'}`)
    img.src = item.fallback
    return
  }

  // Then try default score icon
  if (eventLegendIcons[34]?.src && img.src !== eventLegendIcons[34].src) {
    console.warn('图片加载失败，使用默认图标')
    img.src = eventLegendIcons[34].src
    return
  }

  // Finally try system default
  if (img.src !== imageAssets.defaultScore) {
    console.warn('图片加载失败，使用系统默认图标')
    img.src = imageAssets.defaultScore
  }
}

// 缓存已预加载的图片，避免重复加载
const preloadedImages = new Set<string>()

/**
 * Preload important images to improve performance
 * 使用缓存机制避免重复预加载相同的图片
 */
export const preloadImages = () => {
  const imagesToPreload = [
    imageAssets.defaultScore,
    imageAssets.empty,
    ...Object.values(eventLegendIcons).map((icon: any) => icon.src)
  ]

  imagesToPreload.forEach((src) => {
    if (src && !preloadedImages.has(src)) {
      preloadedImages.add(src)
      const img = new Image()
      img.src = src
      // Optional: Add load/error handlers for debugging
      img.onload = () => console.debug(`Preloaded: ${src}`)
      img.onerror = () => {
        console.warn(`Failed to preload: ${src}`)
        // 如果加载失败，从缓存中移除，允许重试
        preloadedImages.delete(src)
      }
    }
  })
}

/**
 * Get icon source by event type ID
 * @param id - Event type ID
 * @returns Icon source URL
 */
export const getIconSrc = (id: number): string => {
  return (eventLegendIcons[id] || eventLegendIcons[34]).src
}
